import type { SubscriptionTrigger } from "@/lib/tracking";
import type { LoginModalOrigin } from "@/types/loginModal.type";
import type { PlaybackSpeed } from "@/types/player.type";

export const AmplitudeTrackingEvent = {
  episodeView: "Episode View",
  paymentFunnelStep: "Payment Funnel Step",
  purchaseComplete: "Purchase Complete",
  appDownloadClick: "App Download Click",
  googleConversion: "Google Conversion",
  checkoutCohortAssigned: "Checkout Cohort Assigned",
  diagnosticTest: "diagnostic_test",
  emailSubmitted: "email submitted",
  // The login modal, on every opening. It follows the store's `isOpen` and
  // `origin`: an opener that swaps the origin of an open modal counts a second
  // view, one that reopens it from the close callback in the same batch none.
  emailPopUpViewed: "email pop up viewed",
  customSessionStart: "session started",

  // Mobile app events
  libraryViewed: "library viewed",
  openSeries: "open series",
  openSeriesDetails: "open series details",
  tapSkipTrailer: "tap skip trailer",
  episodeStarted: "episode started",
  finishedEpisode: "finished episode",
  didScrollEpisode: "did scroll episode",
  openEpisodeList: "open episode list",
  tapChangeEpisode: "tap change episode",
  openSubtitlesMenu: "open subtitles menu",
  selectSubtitlesLanguage: "select subtitles language",
  openComingSoonPreview: "open coming soon preview",
  openSettings: "open settings",
  showPaywall: "show paywall",
  dismissPaywall: "dismiss paywall",
  purchaseProduct: "purchase product",
  tapPurchaseProduct: "tap purchase product",
  // My List (SHO-653) — event/property names taken verbatim from iOS so web
  // data stacks with mobile. iOS calls the saved-series list "watch list" in
  // tracking even though the UI says "My List".
  saveSeries: "save series",
  unsaveSeries: "unsave series",
  openWatchList: "open watch list",
  // Episode likes (SHO-824), fired from the player's heart after the mutation
  // resolves.
  likeEpisode: "like episode",
  unlikeEpisode: "unlike episode",
  // Series details page (SHO-757): names as on mobile.
  openCastList: "open cast list",
  openCastMember: "open cast member",
  openSeriesDetailsRecommendations: "open series details recommendations",
  tapShareSeries: "tap share series",
  seriesShared: "series shared",
  openBts: "open bts",
  finishBts: "finish bts",
  // The SHO-815 player's controls (SHO-855). Fired by that player only: the
  // control has no gear, no speed and no hold. `mute episode` both players.
  openPlayerSettings: "open player settings",
  selectPlaybackSpeed: "select playback speed",
  holdPlaybackSpeed: "hold playback speed",
  muteEpisode: "mute episode",
  // Its chapter tabs and "Unlock Next Chapter" link (SHO-884): a chapter is
  // not an episode, so neither reports `tap change episode`.
  tapChangeChapter: "tap change chapter",
  tapUnlockNextChapter: "tap unlock next chapter",

  // Upsell flow
  upsellView: "upsell viewed",
  upsellAction: "upsell action",
} as const;

export type AmplitudeTrackingEventType =
  (typeof AmplitudeTrackingEvent)[keyof typeof AmplitudeTrackingEvent];

// How the user moved to another episode (SHO-855). `arrow` is the SHO-815
// player's Next button — the control renders no arrows. `swipe` is the
// control's own navigation, by exclusion like `watched-episodes.md` defines
// it: a swipe, the wheel, the keyboard. `autoplay` is the advance on `ended`,
// `episode list` the episodes panel of either player.
export type ChangeEpisodeOrigin =
  | "arrow"
  | "autoplay"
  | "swipe"
  | "episode list";

// Which surface opened the subtitles menu: the control's mobile rail, the
// SHO-815 settings panel, or the desktop column of either player.
export type SubtitlesMenuOrigin = "settings" | "right rail" | "desktop column";

// How the visitor reached the series details page (SHO-757): carried by
// history state, `seo` when there is none. Known values: `seo`, `series
// details deeplink`, `player breadcrumb`, `player title`, `cast member`
// (a cover on a cast member's page), `series details recommendations` and
// `series details recommendations view all` (a recommended series, from the
// rail or from its View all page), and — since the catalog links to
// the page (SHO-758) — whatever `origin` the same link would have given
// `open series` (`/` for a home rail, `watch list`, `my list trending`…), so
// the two events split by entry point the same way. A plain
// `string`, not the literals plus `string & {}`: TanStack's `HistoryState`
// rejects the intersection as non-serializable.
export type SeriesDetailsOrigin = string;

// `purchase_type` on the `Purchase Complete` event (SHO-588). `onboarding` is
// the classic paywall purchase; `ups *` values tag each upsell screen charge.
export type PurchaseType =
  | "onboarding"
  | "reengage"
  | "ups_vip_pass"
  | "ups_welcome_offer"
  | "ups_yearly_vip"
  | "ups_tip";

export type AmplitudeTrackingEventProperties = {
  [AmplitudeTrackingEvent.episodeView]: {
    "series name": string;
    "episode number": string;
    "episode id": string;
  };
  [AmplitudeTrackingEvent.paymentFunnelStep]: {
    funnel_step: string;
    [key: string]: unknown;
  };
  [AmplitudeTrackingEvent.purchaseComplete]: {
    show_title: string;
    currency: string;
    payment_method?: string;
    revenue?: number;
    subscription_plan: string;
    transaction_id: string;
    provider: string;
    purchase_type: PurchaseType;
    subscription_trigger: SubscriptionTrigger;
  };
  [AmplitudeTrackingEvent.appDownloadClick]: {
    action: string;
    source: string;
  };
  [AmplitudeTrackingEvent.libraryViewed]: {};
  [AmplitudeTrackingEvent.openSeries]: {
    "series name": string;
    origin: string;
  };
  // The series details page becoming visible (SHO-757). `origin` says how the
  // visitor got there: `seo` for a direct landing, `series details deeplink`.
  [AmplitudeTrackingEvent.openSeriesDetails]: {
    "series name": string;
    "displayed series name": string;
    origin: SeriesDetailsOrigin;
  };
  [AmplitudeTrackingEvent.saveSeries]: {
    "series name": string;
    origin: "featured" | "player" | "series details";
  };
  [AmplitudeTrackingEvent.unsaveSeries]: {
    "series name": string;
    origin: "featured" | "player" | "series details" | "watch list";
  };
  [AmplitudeTrackingEvent.likeEpisode]: {
    "series name": string;
    "episode number": string;
    origin: "player";
  };
  [AmplitudeTrackingEvent.unlikeEpisode]: {
    "series name": string;
    "episode number": string;
    origin: "player";
  };
  [AmplitudeTrackingEvent.openWatchList]: {
    "series count": number;
  };
  [AmplitudeTrackingEvent.openCastList]: {
    "series name": string;
    origin: "series details";
  };
  [AmplitudeTrackingEvent.openCastMember]: {
    "series name": string;
    "cast member name": string;
  };
  [AmplitudeTrackingEvent.openSeriesDetailsRecommendations]: {
    "series name": string;
  };
  [AmplitudeTrackingEvent.tapShareSeries]: {
    "series name": string;
    "episode number": string;
    origin: "series details";
  };
  // The Web Share API never says which target was picked, so `web share` is
  // the one value a completed share sheet can report; the desktop fallback
  // reports `copy link`.
  [AmplitudeTrackingEvent.seriesShared]: {
    "series name": string;
    origin: "series details";
    "activity type": "web share" | "copy link";
    "episode number": "none";
  };
  // `bts name` is the source `title`, the identity, like `series name`.
  [AmplitudeTrackingEvent.openBts]: {
    "series name": string;
    "bts name": string;
  };
  [AmplitudeTrackingEvent.finishBts]: {
    "series name": string;
    "bts name": string;
  };
  [AmplitudeTrackingEvent.tapSkipTrailer]: {
    "series name": string;
  };
  [AmplitudeTrackingEvent.episodeStarted]: {
    "series name": string;
    "episode number": string;
  };
  [AmplitudeTrackingEvent.finishedEpisode]: {
    "series name": string;
    "episode number": string;
  };
  [AmplitudeTrackingEvent.didScrollEpisode]: {
    "series name": string;
    "episode number": string;
    completion: number;
  };
  [AmplitudeTrackingEvent.openEpisodeList]: {
    "series name": string;
    "episode number": string;
    origin?: "series details";
  };
  [AmplitudeTrackingEvent.tapChangeEpisode]: {
    "series name": string;
    "from episode": string;
    "to episode": string;
    origin: ChangeEpisodeOrigin;
  };
  [AmplitudeTrackingEvent.openSubtitlesMenu]: {
    "series name": string;
    "episode number": string;
    origin: SubtitlesMenuOrigin;
  };
  [AmplitudeTrackingEvent.openPlayerSettings]: {
    "series name": string;
    "episode number": string;
  };
  [AmplitudeTrackingEvent.selectPlaybackSpeed]: {
    "series name": string;
    "episode number": string;
    "playback speed": PlaybackSpeed;
  };
  [AmplitudeTrackingEvent.holdPlaybackSpeed]: {
    "series name": string;
    "episode number": string;
  };
  [AmplitudeTrackingEvent.muteEpisode]: {
    "series name": string;
    "episode number": string;
    "mute state": "muted" | "unmuted";
  };
  [AmplitudeTrackingEvent.tapChangeChapter]: {
    "series name": string;
    // The tab labels, "1-5" or "6", and "trailer" for the Trailer tab.
    "from chapter": string;
    "to chapter": string;
  };
  [AmplitudeTrackingEvent.tapUnlockNextChapter]: {
    "series name": string;
    "episode number": string;
  };
  [AmplitudeTrackingEvent.selectSubtitlesLanguage]: {
    "series name": string;
    "episode number": string;
    language: string;
  };
  [AmplitudeTrackingEvent.openComingSoonPreview]: {
    "series name": string;
  };
  [AmplitudeTrackingEvent.openSettings]: {};
  [AmplitudeTrackingEvent.emailPopUpViewed]: {
    // The `origin` the opener gave `openLoginModal`.
    origin?: LoginModalOrigin;
  };
  [AmplitudeTrackingEvent.showPaywall]: {
    "series name": string;
    "episode number": string;
    products: Array<string>;
    "default product": string;
    origin: string;
    template: string;
  };
  [AmplitudeTrackingEvent.dismissPaywall]: {
    "series name": string;
    "episode number": string;
    products: Array<string>;
    "default product": string;
    origin: string;
    template: string;
  };
  [AmplitudeTrackingEvent.purchaseProduct]: {
    "series name": string;
    "episode number": string;
    "product id": string;
    products: Array<string>;
    "default product": string;
    origin: string;
    template: string;
  };
  [AmplitudeTrackingEvent.tapPurchaseProduct]: {
    "series name": string;
    "episode number": string;
    "product id": string;
    products: Array<string>;
    "default product": string;
    origin: string;
    template: string;
  };
  [AmplitudeTrackingEvent.upsellView]: {
    upsell_name: string;
  };
  [AmplitudeTrackingEvent.upsellAction]: {
    step: number;
    product: string;
    action: "skip" | "continue";
  };
  [key: string]: Record<string, unknown>;
};
